import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format';
import type { InvoiceList } from '@/routes/invoices/useInvoices';

/**
 * The A/R aging strip (M2-12): the five buckets the API returns, in its order,
 * each with its count and amount — even at zero, because a bucket that
 * vanished when empty would move the others under the office's eye.
 *
 * Each bucket is a toggle (`aria-pressed`) that narrows the ROWS to it. The
 * strip itself does not change when one is pressed: the API computes it from
 * the customer and sent-date filters only (pass A's decision), so pressing
 * "31–60" leaves every bucket's figure standing and shows which one is on.
 */
export function AgingStrip({
  aging,
  selected,
  onSelect,
}: {
  aging: InvoiceList['aging'];
  selected: string;
  onSelect: (bucket: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <section aria-labelledby="invoice-aging-heading" className="space-y-2">
      <h2 id="invoice-aging-heading" className="text-sm font-medium">
        {t('invoices.aging.heading')}
      </h2>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="aging-strip">
        {aging.map((row) => {
          const pressed = selected === row.bucket;
          const count = Number(row.count);
          return (
            <li key={row.bucket}>
              <button
                type="button"
                aria-pressed={pressed}
                data-testid={`aging-${row.bucket}`}
                onClick={() => onSelect(pressed ? '' : row.bucket)}
                className={cn(
                  'w-full rounded-md border px-3 py-2 text-left text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none',
                  pressed
                    ? 'border-[var(--color-primary)] bg-[var(--color-muted)]'
                    : 'border-[var(--color-border)] hover:bg-[var(--color-muted)]',
                )}
              >
                <span className="block font-medium">
                  {t(`invoices.aging.bucket.${row.bucket}`, { defaultValue: row.bucket })}
                </span>
                <span className="block tabular-nums" data-testid="aging-amount">
                  {formatCurrency(Number(row.amount))}
                </span>
                <span className="block text-xs text-[var(--color-muted-foreground)]">
                  {t('invoices.aging.count', { count })}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
